import React, { useState, useEffect, useRef } from "react";
import httpService from "../../common/httpService";
import "./style.scss";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DataGrid } from "@mui/x-data-grid";
import { backendUrlImages } from "../../constant/config";

const VisasComponent = () => {
  const [visasData, setVisasData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [errorMsg, setErrorMsg] = useState("");
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [selectedVisa, setSelectedVisa] = useState(null);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const TOKEN = localStorage.getItem("token");
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const hasShownApiAlert = useRef(false);

  const openMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const DEFAULT_ERROR_MESSAGE = "Failed to fetch visas.";

  const getSpeakerVisaFee = async (speakerId) => {
    try {
      const apiUrl = `https://eventscons.com/eventbackendnew/api/speaker/visa/fee/${speakerId}`;
      const response = await fetch(apiUrl);

      if (!response.ok) {
        console.log("Visa fee API failed:", {
          speakerId,
          status: response.status,
          url: apiUrl,
        });
        return null;
      }

      const data = await response.json();

      console.log("Visa fee API success:", {
        speakerId,
        url: apiUrl,
        response: data,
      });

      return data;
    } catch (error) {
      console.log("Visa fee API error:", {
        speakerId,
        error,
      });

      return null;
    }
  };

  const fetchVisas = async (page = 1) => {
    try {
      await httpService({
        method: "GET",
        url: `${BASE_URL}/all/visas`,
        params: { page },
        headers: { Authorization: `Bearer ${TOKEN}` },
        onSuccess: async (data) => {
          const visas = data?.visas?.reverse() || [];

          const updatedVisas = await Promise.all(
            visas.map(async (visa) => {
              const speakerId = visa.user_id;
              const visaFeeResponse = await getSpeakerVisaFee(speakerId);
              const visaFeesValue = visaFeeResponse?.visa_fees;

              console.log("Matching visa row:", {
                visaId: visa.id,
                user_id: visa.user_id,
                speakerIdUsed: speakerId,
                originalVisaCost: visa.visa_cost,
                visaFeeResponse,
              });

              const isCovered =
                typeof visaFeesValue === "string" &&
                visaFeesValue.trim().toLowerCase() === "covered";

              return {
                ...visa,
                visa_cost: isCovered ? 0 : Number(visa.visa_cost || 0),
              };
            })
          );

          setVisasData(updatedVisas);
          setTotalPages(data?.totalPages || 1);
          setCurrentPage(Number(data?.currentPage) || 1);
          setErrorMsg("");
        },
        onError: (error) => {
          setErrorMsg(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
        },
        withToast: false,
      });
    } catch (error) {
      setErrorMsg(DEFAULT_ERROR_MESSAGE);
    }
  };

  const handlePageChange = (page) => {
    fetchVisas(page);
  };

  const handleViewVisaDetails = (visa) => {
    setSelectedVisa(visa);
    setDrawerOpen(true);
    closeMenu();
  };

  useEffect(() => {
    fetchVisas();
  }, []);

  const rows = visasData.map((visa) => ({
    ...visa,
    id: visa.id,
    user_name: visa.user_name,
    user_id: visa.user_id,
    passport_image: visa?.passport_image,
    arrival_date: visa.arrival_date,
    departure_date: visa.departure_date,
    visa_cost: Number(visa.visa_cost || 0),
    payment_status_display:
      Number(visa.visa_cost || 0) === 0 ? "covered" : visa.payment_status || "-",
    status: visa.status,
    actions: "actions",
  }));

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 1,
      minWidth: 230,
      cellClassName: "centered-cell",
    },
    {
      field: "user_id",
      headerName: "UserID",
      flex: 1,
      minWidth: 230,
      cellClassName: "centered-cell",
    },
    {
      field: "user_name",
      headerName: "User Name",
      flex: 1,
      minWidth: 230,
      cellClassName: "centered-cell",
    },
    {
      field: "passport_image",
      headerName: "Passport Image",
      flex: 1,
      minWidth: 230,
      cellClassName: "centered-cell",
      renderCell: (params) => (
        <div style={{ textAlign: "center" }}>
          {params.row.passport_image ? (
            <Typography
              variant="body2"
              color="primary"
              style={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => {
                const imageUrl = `${backendUrlImages}${params.row.passport_image}`;
                const link = document.createElement("a");
                link.href = imageUrl;
                link.download = params.row.passport_image;
                link.click();
              }}
            >
              Download Image
            </Typography>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No Image
            </Typography>
          )}
        </div>
      ),
    },
    {
      field: "arrival_date",
      headerName: "Arrival Date",
      flex: 1,
      minWidth: 230,
      cellClassName: "centered-cell",
    },
    {
      field: "departure_date",
      headerName: "Departure Date",
      flex: 1,
      minWidth: 230,
      cellClassName: "centered-cell",
    },
    {
      field: "visa_cost",
      headerName: "Visa Cost",
      flex: 1,
      minWidth: 230,
      cellClassName: "centered-cell",
      renderCell: (params) => <span>${Number(params.row.visa_cost || 0)}</span>,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 230,
      cellClassName: "centered-cell",
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 230,
      cellClassName: "centered-cell",
      renderCell: (params) => (
        <>
          <IconButton onClick={(event) => openMenu(event, params.row)}>
            <MoreVertIcon />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && selectedRow?.id === params.row.id}
            onClose={closeMenu}
          >
            <MenuItem onClick={() => handleViewVisaDetails(params.row)}>
              View Details
            </MenuItem>

            {params.row.status === "pending" && (
              <MenuItem
                onClick={() => {
                  navigate(`/admin/visa2/${params.row?.user_id}`);
                  closeMenu();
                }}
              >
                Submit
              </MenuItem>
            )}
          </Menu>
        </>
      ),
    },
  ];

  return (
    <div
      className="visas-component"
      style={{
        borderRadius: "8px",
        width: "100%",
        maxWidth: "1700px",
        padding: "20px",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: "#c62828",
          fontWeight: "bold",
          fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
          textAlign: "center",
        }}
      >
        Visa
      </Typography>

      {errorMsg && (
        <Typography
          variant="body2"
          sx={{ color: "red", textAlign: "center", mt: 2 }}
        >
          {errorMsg}
        </Typography>
      )}

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        paginationModel={{ page: currentPage - 1, pageSize: 12 }}
        onPaginationModelChange={(pagination) => {
          setCurrentPage(pagination.page + 1);
          handlePageChange(pagination.page + 1);
        }}
        rowCount={totalPages * 12}
        pageSizeOptions={[12]}
        paginationMode="server"
        checkboxSelection
        disableRowSelectionOnClick
        autoHeight
        sx={{
          marginTop: "20px",
          "& .MuiDataGrid-virtualScroller": {
            overflow: "hidden",
          },
        }}
      />

      <Drawer
        open={isDrawerOpen}
        onClose={() => setDrawerOpen(false)}
        anchor="right"
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          "& .MuiDrawer-paper": {
            zIndex: (theme) => theme.zIndex.modal + 1,
            width: {
              xs: "100%",
              sm: "50%",
              md: "40%",
              lg: "30%",
              xl: "30%",
            },
          },
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: 2,
          }}
        >
          <IconButton onClick={() => setDrawerOpen(false)}>
            <CloseRounded />
          </IconButton>
        </div>

        <Card elevation={4} style={{ margin: "16px 0", borderRadius: "8px" }}>
          <CardContent>
            <Typography
              variant="h5"
              component="div"
              style={{
                fontWeight: "bold",
                marginBottom: "16px",
                textAlign: "center",
                color: "#c62828",
              }}
            >
              Visa Details
            </Typography>

            <Divider style={{ marginBottom: "16px" }} />

            {selectedVisa ? (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={600}>
                      User ID:
                    </Typography>
                    <Typography variant="body1" ml={2}>
                      {selectedVisa.user_id || "-"}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={600}>
                      Arrival Date:
                    </Typography>
                    <Typography variant="body1" ml={2}>
                      {selectedVisa.arrival_date || "-"}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={600}>
                      Departure Date:
                    </Typography>
                    <Typography variant="body1" ml={2}>
                      {selectedVisa.departure_date || "-"}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={600}>
                      Visa Cost:
                    </Typography>
                    <Typography variant="body1" ml={2}>
                      ${Number(selectedVisa.visa_cost || 0)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={600}>
                      Payment Required:
                    </Typography>
                    <Typography variant="body1" ml={1}>
                      {selectedVisa.payment_required ? "Yes" : "No"}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={600}>
                      Status:
                    </Typography>
                    <Typography variant="body1" ml={1}>
                      {selectedVisa.status || "-"}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={600}>
                      Payment Method:
                    </Typography>
                    <Typography variant="body1" ml={1}>
                      {selectedVisa.payment_method || "-"}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={600}>
                      Payment Status:
                    </Typography>
                    <Typography variant="body1" ml={1}>
                      {Number(selectedVisa.visa_cost || 0) === 0
                        ? "covered/paid"
                        : selectedVisa.payment_status || "-"}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={600}>
                      Transaction Id:
                    </Typography>
                    <Typography variant="body1" ml={1}>
                      {selectedVisa.transaction_id || "-"}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={600}>
                      Payment Date:
                    </Typography>
                    <Typography variant="body1" ml={1}>
                      {selectedVisa.payment_date || "-"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Typography
                variant="body1"
                color="textSecondary"
                style={{ textAlign: "center" }}
              >
                No visa details available.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Drawer>
    </div>
  );
};

export default VisasComponent;